import clsx from 'clsx';
import {
  type FocusEvent,
  FocusEventHandler,
  forwardRef,
  type KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import { XmarkIcon } from '@dynatrace/strato-icons';

import { DateSelector } from './DateSelector.js';
import * as styles from './DateTimePicker.sty.js';
import type { DateSelectorRef, DateTimePickerProps } from './types.js';
import { focusDateTimeInput } from './utils/focusDateTimeInput.js';
import { Button } from '../../buttons/button/Button.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useFocusRing } from '../../core/hooks/useFocusRing.js';
import { useFormControlState } from '../../core/hooks/useFormControlState.js';
import type { TimeErrorKey } from '../../core/time/shared.types.js';
import { useDateTimePickerStore } from '../../core/time/timeframeStore/useTimeframeStore.js';
import {
  formatTimeValidationMessage,
  getValidityStateKeyForTimeError,
} from '../../core/time/validation.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { inputGroupClassName } from '../../layouts/input-group/InputGroup.sty.js';
import { InputGroup } from '../../layouts/input-group/InputGroup.js';
import { fieldTokens } from '../../styles/field.sty.js';
import { DateTimeInput } from '../date-time-range-picker/DateTimeInput.js';
import { dateTimeRange } from '../date-time-range-picker/DateTimeRangePicker.sty.js';
import { isExpressionTriggerKey } from '../date-time-range-picker/expression-trigger-helpers.js';
import { isContentModifyingKey } from '../date-time-range-picker/helpers.js';
import { onTimeframeCopyCapture } from '../date-time-range-picker/selection-copy-helpers.js';
import { useSelectionInteractionHandlers } from '../date-time-range-picker/useSelectionInteractionHandlers.js';
import { InternalBaseItem } from '../forms-core/components/form-field-messages/FormFieldMessageBaseItem.js';
import { useFormFieldMessagesContext } from '../forms-core/hooks/useFormFieldMessagesContext.js';
import { useMinMaxLogger } from '../forms-core/hooks/useMinMaxLogger.js';
import { useSyncedFormControlProps } from '../forms-core/hooks/useSyncedFormControlProps.js';
import { FormFieldMessagesAction } from '../forms-core/state/formfield-messages-state-reducer.js';
import { useInputHint } from '../hooks/useInputHint.js';
import type { FormControlWithOverlayRef } from '../shared-types.js';
import {
  type ValidationState,
  type ValidationTrigger,
} from '../validation/types.js';
import { useCustomMessageEmitter } from '../validation/useCustomMessageEmitter.js';
import { useValidationStateEmitter } from '../validation/useValidationStateEmitter.js';
import { getValidationState } from '../validation/validation-builders.js';

const COMPONENT_NAME = 'DateTimePicker';

/**
 * Component to comfortably enter a date time value.
 * The `DateTimePicker` component automatically uses the user's settings for the
 * locale and timezone and formats the date and time accordingly.
 * @internal
 */
export const DateTimePickerRoot = forwardRef<
  FormControlWithOverlayRef<HTMLSpanElement>,
  DateTimePickerProps
>((props, forwardedRef) => {
  const {
    id: propId,
    disabled: propDisabled,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    readOnly = false,
    required: propRequired,
    'aria-disabled': ariaDisabledProp,
    placeholder,
    name,
    type = 'datetime',
    controlState,
    onValidityChange,
    ...remainingProps
  }: DateTimePickerProps = props;

  const [behavioralTrackingProps] = useBehavioralTrackingProps(remainingProps);

  const min = useDateTimePickerStore((state) => state.min);
  const max = useDateTimePickerStore((state) => state.max);
  const precision = useDateTimePickerStore((state) => state.precision);
  const formatString = useDateTimePickerStore((state) => state.formatString);
  const value = useDateTimePickerStore((state) => state.dateTimePickerValue);
  const dateTimePickerError = useDateTimePickerStore(
    (state) => state.dateTimePickerError,
  );

  const setValue = useDateTimePickerStore(
    (state) => state.setDateTimePickerValue,
  );
  const dateInputRef = useDateTimePickerStore(
    (state) => state.dateTimeInputRef,
  );

  const rootRef = useRef<HTMLDivElement>(null);
  const dateSelectorRef = useRef<DateSelectorRef>(null);

  const [hasInputContent, setHasInputContent] = useState(false);

  // log to the sdk when min is larger than max.
  useMinMaxLogger(min, max, 'DateTimePicker');

  const touchedContainerRef = useRef<HTMLDivElement>(null);

  const { state: formFieldState, dispatch: formFieldDispatch } =
    useFormFieldMessagesContext();
  const { showError: showFormFieldError, isTouched } = formFieldState;

  // Backwards compatibility for controlState.
  const [hasFormControlError, hint] = useFormControlState(controlState);
  // _useInputHint will be replaced with new validation concept. APPDEV-8298
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const { hintProps } = useInputHint({
    hint,
    hasError: hasFormControlError,
  });

  const showError = hasFormControlError || showFormFieldError;

  const { disabled, required, id, ariaDisabled } = useSyncedFormControlProps({
    propDisabled,
    propRequired,
    propId,
    propAriaDisabled: ariaDisabledProp,
  });
  const disabledOrAriaDisabled = disabled || ariaDisabled || false;

  const ariaLabelingProps = useAriaLabelingProps(remainingProps);

  // No need to show the calendar widget if only a time value can be entered.
  const showDateSelector = type !== 'time';

  const { focusClassName } = useFocusRing({
    focusWithin: true,
    ignoreModality: true,
    color: showError ? 'critical' : 'neutral',
    disabled,
  });

  const intl = useIntl();
  const calendarTriggerLabel = intl.formatMessage({
    defaultMessage: 'Datepicker calendar trigger',
    id: 'Uuc6xAAPyctyHamM',
    description: 'Text shown for the datetimepicker calendar trigger.',
  });
  const clearButtonLabel = intl.formatMessage({
    defaultMessage: 'Clear date and time',
    id: '5i26Rr1LLz0OIGNJ',
    description: 'Aria label for the DateTimePicker clear button.',
  });

  const _emitValidationState = useValidationStateEmitter(onValidityChange);
  const emitValidationState = useCustomMessageEmitter(
    _emitValidationState,
    () => buildValidationState('manual', dateTimePickerError),
  );

  const buildValidationState = useCallback(
    (
      trigger: ValidationTrigger,
      key: TimeErrorKey | undefined,
    ): ValidationState => {
      if (key === undefined) {
        return { status: 'done', trigger, errors: [] };
      }

      const keyInstance = getValidityStateKeyForTimeError(key);

      return getValidationState(
        keyInstance,
        trigger,
        formatTimeValidationMessage(key, intl, {
          value,
          min,
          max,
          precision,
          expectedFormat: formatString,
        }),
        id || undefined,
      );
    },
    [intl, value, min, max, precision, formatString, id],
  );

  // Provide root and input element reference and functions to interact with the calendar overlay
  useImperativeHandle(
    forwardedRef,
    (): FormControlWithOverlayRef<HTMLSpanElement> => ({
      element: rootRef.current,
      inputRef: dateInputRef.current?.inputRef,
      triggerRef: dateInputRef.current?.inputRef,
      open: () => dateSelectorRef.current?.open(),
      close: () => dateSelectorRef.current?.close(),
      focus() {
        dateInputRef.current?.inputRef?.focus();
      },
      validate() {
        dateInputRef.current?.validate();
        formFieldDispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: true },
        });
        emitValidationState(
          buildValidationState('manual', dateTimePickerError),
        );
      },
      // DEPRECATION: [APPDEV-18178] BREAKING-CHANGE: Remove validateAsync() and change validate() to return Promise<ValidationState>.
      validateAsync: async () => {
        dateInputRef.current?.validate();
        formFieldDispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: true },
        });
        const state = buildValidationState('manual', dateTimePickerError);
        return emitValidationState(state);
      },
    }),
    [
      formFieldDispatch,
      dateInputRef,
      emitValidationState,
      buildValidationState,
    ],
  );

  // mark the component as touched, if the touchedRef container is losing the focus
  const onBlurTouched: FocusEventHandler = useCallback(
    (e) => {
      if (!touchedContainerRef.current?.contains(e.relatedTarget)) {
        formFieldDispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: true },
        });
        emitValidationState(buildValidationState('blur', dateTimePickerError));
      }
    },
    [
      formFieldDispatch,
      isTouched,
      dateTimePickerError,
      emitValidationState,
      buildValidationState,
    ],
  );

  // remove the textContent when the value is set to null.
  useEffect(() => {
    if (
      value === null &&
      dateInputRef.current?.expressionInputRef &&
      dateInputRef.current.expressionInputRef.textContent !== '' &&
      dateInputRef.current.element?.getAttribute('data-valuetype') !==
        'expression'
    ) {
      dateInputRef.current.expressionInputRef.textContent = '';
    }
  }, [value, dateInputRef]);

  useEffect(() => {
    emitValidationState(buildValidationState('change', dateTimePickerError));
  }, [value, emitValidationState, buildValidationState, dateTimePickerError]);

  const {
    onMouseDown,
    onKeyDown: handleSelectionKeyDown,
    onCutCapture,
    onContextMenu,
  } = useSelectionInteractionHandlers(
    touchedContainerRef,
    useCallback(() => {
      focusDateTimeInput(null, dateInputRef);
    }, [dateInputRef]),
    dateInputRef,
  );

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (disabled || readOnly) {
        handleSelectionKeyDown(event);
        return;
      }

      const isIso =
        dateInputRef.current?.element?.getAttribute('data-valuetype') ===
        'iso8601';

      if (isExpressionTriggerKey(event) && isIso) {
        dateInputRef.current?.setExpressionValue(event.key);
        requestAnimationFrame(() => {
          const expressionInputRef = dateInputRef.current?.expressionInputRef;
          if (expressionInputRef) {
            expressionInputRef.focus();
            const selection = window.getSelection();
            if (selection) {
              const range = document.createRange();
              range.selectNodeContents(expressionInputRef);
              range.collapse(false);
              selection.removeAllRanges();
              selection.addRange(range);
            }
          }
        });
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (isContentModifyingKey(event)) {
        dateInputRef.current?.clearPasteUndo();
      }

      handleSelectionKeyDown(event);
    },
    [disabled, readOnly, handleSelectionKeyDown, dateInputRef],
  );

  return (
    <Flex
      gap={4}
      flexDirection="column"
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      ref={rootRef}
      className={consumerClassName}
      style={consumerStyle}
      {...behavioralTrackingProps}
      data-dt-component={COMPONENT_NAME}
    >
      <InputGroup
        className={clsx(
          styles.dateTimePicker({
            type,
            disabled: disabledOrAriaDisabled,
            showError,
            readOnly,
          }),
        )}
        style={{ gridAutoColumns: '1fr auto' }}
      >
        <div
          ref={touchedContainerRef}
          className={clsx(
            fieldTokens({
              color: showError ? 'critical' : 'neutral',
              disabled: disabledOrAriaDisabled,
              variant: 'emphasized',
            }),
            dateTimeRange({
              showError,
              disabled: disabledOrAriaDisabled,
              readOnly,
            }),
            focusClassName,
            inputGroupClassName,
          )}
          tabIndex={disabled ? undefined : 0}
          onBlur={onBlurTouched}
          onInput={() => {
            setHasInputContent(
              !!dateInputRef.current?.expressionInputRef?.textContent,
            );
          }}
          onMouseDown={onMouseDown}
          onKeyDown={onKeyDown}
          onFocus={(e: FocusEvent) => {
            // Forward the focus only if it is coming from outside this component.
            // Focus the first spin button with textContent; otherwise, focus the input.
            if (!rootRef.current?.contains(e.relatedTarget)) {
              focusDateTimeInput(e, dateInputRef);
            }
          }}
          onCopyCapture={onTimeframeCopyCapture}
          onCutCapture={onCutCapture}
          onContextMenu={onContextMenu}
        >
          <DateTimeInput
            ref={dateInputRef}
            id={id}
            type={type}
            disabled={disabled}
            readOnly={readOnly}
            placeholder={placeholder}
            name={name}
            value={value}
            onChange={(newValue) => {
              if (typeof newValue !== 'string') {
                setValue(newValue);
              }
            }}
            {...ariaLabelingProps}
            aria-disabled={ariaDisabled}
            required={required}
            validationValueMode="single"
            controlId={id}
            controlState={controlState}
            onPasteComplete={() => {
              requestAnimationFrame(() => {
                dateInputRef.current?.focusLast();
              });
            }}
          />
          {!readOnly &&
            !disabledOrAriaDisabled &&
            ((value !== null && value.value !== '') || hasInputContent) && (
              <Button
                onFocus={() => {
                  formFieldDispatch?.({
                    type: FormFieldMessagesAction.SETISTOUCHED,
                    payload: { isTouched: true },
                  });
                }}
                onClick={() => {
                  setValue(null);
                  if (dateInputRef.current?.expressionInputRef) {
                    dateInputRef.current.expressionInputRef.textContent = '';
                  }
                  setHasInputContent(false);
                  requestAnimationFrame(() => {
                    dateInputRef.current?.expressionInputRef?.focus();
                  });
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.stopPropagation();
                  }
                }}
                className={styles.clearIconButton}
                color={showError ? 'critical' : 'neutral'}
                size="condensed"
                aria-label={clearButtonLabel}
              >
                <XmarkIcon />
              </Button>
            )}
        </div>
        {showDateSelector && (
          <DateSelector
            ref={dateSelectorRef}
            aria-label={calendarTriggerLabel}
            aria-disabled={ariaDisabled}
            readOnly={readOnly}
            disabled={!!disabled}
            error={showError}
            className={clsx(styles.dateSelector, inputGroupClassName)}
            data-dtrum-mask={dataDtrumMask}
            data-dtrum-allow={dataDtrumAllow}
          />
        )}
      </InputGroup>
      {hint && (
        <InternalBaseItem
          variant={hasFormControlError ? 'error' : 'hint'}
          disabled={disabled || ariaDisabled}
          {...hintProps}
        >
          {hint}
        </InternalBaseItem>
      )}
    </Flex>
  );
});

(
  DateTimePickerRoot as typeof DateTimePickerRoot & { displayName: string }
).displayName = COMPONENT_NAME;
